import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';

import Iconify from '../../theme/iconify/iconify';

import { useResponsive } from '../../hooks/use-responsive';

import { LgTabs, SmTabs } from '../util/Tabs';
import Notification from '../util/Notification';
import ConfirmationModal from '../util/ConfirmationModal';
import { educationList, employmentList, genderList, skillList } from '../util/form-values';

import ChangePassword from './ChangePassword';
import { mislecAPI } from '../../util/axios';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
};

function Profile({ user, updateUser }) {
    const [fname, setFname] = React.useState(user ? user.fname : '');
    const [lname, setLname] = React.useState(user ? user.lname : '');
    const [email, setEmail] = React.useState(user ? user.email : '');
    const [age, setAge] = React.useState(user && user.age ? user.age : '');
    const [ageError, setAgeError] = React.useState(false);
    const [gender, setGender] = React.useState(user && user.gender ? user.gender : '');
    const [education, setEducation] = React.useState(user && user.education ? user.education : '');
    const [employment, setEmployment] = React.useState(user && user.employment ? user.employment : '');
    const [studentID, setStudentID] = React.useState(user && user.student_id ? user.student_id : '');
    const [skills, setSkills] = React.useState(user && user.skills ? user.skills : []);
    const [password, setPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [tab, setTab] = React.useState(0);
    const [deleteUserModal, setDeleteUserModal] = React.useState(false);
    const [changePasswordModal, setChangePasswordModal] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const { t } = useTranslation();
    const lgUp = useResponsive('up', 'lg');

    React.useEffect(() => {
        setFname(user ? user.fname : '');
        setLname(user ? user.lname : '');
        setEmail(user ? user.email : '');
        setAge(user && user.age ? user.age : '');
        setGender(user && user.gender ? user.gender : '');
        setEducation(user && user.education ? user.education : '');
        setEmployment(user && user.employment ? user.employment : '');
        setSkills(user && user.skills ? user.skills : []);
    }, [user]);

    //  Tab configuration
    const config = [
        {
            label: t('profile.tabs.profile.title')
        },
        {
            label: t('profile.tabs.security.title')
        }
    ]

    const handleAgeChange = (value) => {
        if (!/^\d*$/.test(value)) {
            setAgeError(true);
        } else {
            setAgeError(false);
        }

        setAge(value);
    }

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    }

    const update = () => {
        if (!password) {
            //  Notify user of missing password
            setMessage(t('profile.tabs.profile.basic.error.password'));
            setSeverity('warning');
            setOpen(true);
            return;
        }

        if (ageError) {
            //  Notify user of invalid age
            setMessage(t('profile.tabs.profile.basic.error.age'));
            setSeverity('warning');
            setOpen(true);
            return;
        }

        if (user) {
            //  Request body
            const data = {
                fname: fname,
                lname: lname,
                username: user.username,
                email: email,
                age: age ? parseInt(age) : null,
                gender: gender,
                student_id: studentID,
                education: education,
                employment: employment,
                skills: skills,
                password: password
            };

            // Send data to server
            mislecAPI.put(`/api/users/${user._id}`, data)
                .then(res => {
                    //  Notify user of successful update
                    setMessage(t(res.data.detail));
                    setSeverity('success');
                    setOpen(true);
                    updateUser(res.data.user);
                })
                .catch(error => {
                    if (error.response.data.detail === 'api.auth.verify.401') {
                        //  Expired session
                        updateUser(null);
                    } else {
                        //  Notify user of error
                        setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                        setSeverity('error');
                        setOpen(true);
                    }
                });
        }
    }

    const deleteUser = () => {
        //  Remove user from the server
        mislecAPI.delete(`/api/users/${user._id}`)
            .then(res => {
                //  Notify user of successful deletion
                setDeleteUserModal(false);
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                setTimeout(() => {
                    sessionStorage.removeItem('mISLec_access_token');
                    updateUser(null);
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setDeleteUserModal(false);
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const renderEditProfile = () => {
        return (
            <Grid container sx={{ ml: 1 }}>
                <Grid container spacing={3} sx={{ mt: 0, pt: 0 }}>
                    <Grid item xs={12} sx={{ pt: '0px !important' }}>
                        <Typography variant='h5'>{t('profile.tabs.profile.title')}</Typography>
                        <Typography variant='body2' sx={{ mt: 1 }}>
                            {t('profile.tabs.profile.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2, pt: '16px !important' }}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '0px !important' }}>
                        <Typography variant='h6'>{t('profile.tabs.profile.basic.title')}</Typography>
                        <Typography variant='body2' sx={{ mt: 1, mb: 1 }}>
                            {t('profile.tabs.profile.basic.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ pt: { lg: '16px !important' } }}>
                        <TextField
                            id='mISLec_fname'
                            name='fname'
                            label={t('profile.tabs.profile.basic.inputs.fname.label')}
                            value={fname}
                            onChange={(e) => setFname(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ pt: { lg: '16px !important' } }}>
                        <TextField
                            id='mISLec_lname'
                            name='lname'
                            label={t('profile.tabs.profile.basic.inputs.lname.label')}
                            value={lname}
                            onChange={(e) => setLname(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} md={2} sx={{ pt: { lg: '16px !important' } }}>
                        <TextField
                            id='mISLec_age'
                            name='age'
                            label={t('profile.tabs.profile.basic.inputs.age.label')}
                            value={age}
                            onChange={(e) => handleAgeChange(e.target.value)}
                            error={ageError}
                            helperText={ageError ? t('profile.tabs.profile.basic.inputs.helper.numeric') : ''}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth>
                            <InputLabel id='mISLec_profile_gender_label'>{t('profile.tabs.profile.basic.inputs.gender.label')}</InputLabel>
                            <Select
                                id='mISLec_profile_gender'
                                labelId='mISLec_profile_gender_label'
                                label={t('profile.tabs.profile.basic.inputs.gender.label')}
                                value={gender}
                                onChange={(e) => setGender(e.target.value)}
                            >
                                {genderList.map((gender, index) => (
                                    <MenuItem key={index} value={gender}>
                                        <Typography variant='body2'>{t(`profile.tabs.profile.basic.inputs.gender.values.${gender}`)}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            id='mISLec_student_id'
                            name='student_id'
                            label={t('profile.tabs.profile.basic.inputs.studentID.label')}
                            value={studentID}
                            onChange={(e) => setStudentID(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel id='mISLec_profile_education_label'>{t('profile.tabs.profile.basic.inputs.education.label')}</InputLabel>
                            <Select
                                id='mISLec_profile_education'
                                labelId='mISLec_profile_education_label'
                                label={t('profile.tabs.profile.basic.inputs.education.label')}
                                value={education}
                                onChange={(e) => setEducation(e.target.value)}
                            >
                                {educationList.map((education, index) => (
                                    <MenuItem key={index} value={education}>
                                        <Typography variant='body2'>{t(`profile.tabs.profile.basic.inputs.education.values.${education}`)}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel id='mISLec_profile_employment_label'>{t('profile.tabs.profile.basic.inputs.employment.label')}</InputLabel>
                            <Select
                                id='mISLec_profile_employment'
                                labelId='mISLec_profile_employment_label'
                                label={t('profile.tabs.profile.basic.inputs.employment.label')}
                                value={employment}
                                onChange={(e) => setEmployment(e.target.value)}
                            >
                                {employmentList.map((employment, index) => (
                                    <MenuItem key={index} value={employment}>
                                        <Typography variant='body2'>{t(`profile.tabs.profile.basic.inputs.employment.values.${employment}`)}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormControl fullWidth>
                            <InputLabel id='mISLec_profile_skills_label'>{t('profile.tabs.profile.basic.inputs.skills.label')}</InputLabel>
                            <Select
                                id='mISLec_profile_skills'
                                labelId='mISLec_profile_skills_label'
                                label={t('profile.tabs.profile.basic.inputs.skills.label')}
                                value={skills}
                                onChange={(e) => setSkills(e.target.value)}
                                multiple
                                renderValue={(selected) => {
                                    const displayLimit = 10;
                                    const displayItems = selected.slice(0, displayLimit);
                                    const hiddenCount = selected.length - displayLimit;

                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                alignItems: 'center'
                                            }}
                                        >
                                            {displayItems.map((skill) => (
                                                <Chip key={skill} label={t(`profile.tabs.profile.basic.inputs.skills.values.${skill}`)} />
                                            ))}
                                            {hiddenCount > 0 && (
                                                <span style={{ fontSize: '0.8125rem' }}>{`+${hiddenCount} ${t('profile.tabs.profile.basic.inputs.skills.more')}`}</span>
                                            )}
                                        </Box>
                                    );
                                }}
                                MenuProps={MenuProps}
                            >
                                {skillList.map((skill, index) => (
                                    <MenuItem key={index} value={skill}>
                                        <Typography variant='body2'>{t(`profile.tabs.profile.basic.inputs.skills.values.${skill}`)}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 2, pt: '24px !important' }}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '0px !important' }}>
                        <Typography variant='h6'>{t('profile.tabs.profile.account.title')}</Typography>
                        <Typography variant='body2' sx={{ mt: 1, mb: 1 }}>
                            {t('profile.tabs.profile.account.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ pt: { lg: '16px !important' } }}>
                        <TextField
                            id='mISLec_username'
                            name='username'
                            label={t('profile.tabs.profile.account.inputs.username')}
                            value={user ? user.username : ''}
                            disabled
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={9} sx={{ pt: { lg: '16px !important' } }}>
                        <TextField
                            id='mISLec_email'
                            name='email'
                            label={t('profile.tabs.profile.account.inputs.email')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id='mISLec_password'
                            name='password'
                            label={t('profile.tabs.profile.account.inputs.password')}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end' sx={{ mr: 1 }}>
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge='end' sx={{ borderRadius: '8px' }}>
                                            <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }} >
                        <Button
                            variant=''
                            onClick={() => update()}
                            sx={{
                                borderRadius: '8px !important',
                                minWidth: {
                                    xs: '100%',
                                    md: 140
                                },
                                maxWidth: {
                                    xs: '100%',
                                    md: 140
                                },
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.primary.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            {t('profile.tabs.profile.account.buttons.save')}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    const renderAccount = () => {
        return (
            <Grid container sx={{ mt: 0, mb: 2, pt: 0, ml: 1 }}>
                <Grid item xs={12} sx={{ pt: '0px !important' }}>
                    <Typography variant='h5'>{t('profile.tabs.security.password.title')}</Typography>
                    <Typography variant='body2' sx={{ mt: 1 }}>
                        {t('profile.tabs.security.password.description')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', pt: '16px !important' }} >
                    <Stack
                        sx={{
                            width: {
                                xs: '100%',
                                md: 200
                            }
                        }}
                    >
                        <Button
                            variant=''
                            onClick={() => setChangePasswordModal(true)}
                            sx={{
                                ml: {
                                    xs: 0,
                                    md: 1
                                },
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.primary.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            {t('profile.tabs.security.password.buttons.change')}
                        </Button>
                    </Stack>
                </Grid>
                <Grid item xs={12} sx={{ mb: 2, mt: 3 }}>
                    <Divider />
                </Grid>
                <Grid item xs={12} sx={{ pt: '0px !important' }}>
                    <Typography variant='h5'>{t('profile.tabs.security.terminate.title')}</Typography>
                    <Typography variant='body2' sx={{ mt: 1 }}>
                        {t('profile.tabs.security.terminate.description')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', pt: '16px !important' }} >
                    <Stack
                        sx={{
                            width: {
                                xs: '100%',
                                md: 240
                            }
                        }}
                    >
                        <Button
                            variant=''
                            fullWidth
                            onClick={() => setDeleteUserModal(true)}
                            sx={{
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                                color: {
                                    xs: 'error.main'
                                },
                                '&:hover': {
                                    color: 'error.main',
                                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                                }
                            }}
                        >
                            {t('profile.tabs.security.terminate.buttons.terminate')}
                        </Button>
                    </Stack>
                </Grid>
                <ChangePassword
                    user={user}
                    updadeUser={updateUser}
                    open={changePasswordModal}
                    onClose={() => setChangePasswordModal(false)}
                    setOpen={setOpen}
                    setMessage={setMessage}
                    setSeverity={setSeverity}
                    setChangePasswordModal={setChangePasswordModal}
                />
            </Grid>
        );
    }

    return (
        <>
            <Container>
                <Grid item xs={12} mb={2}>
                    <Typography variant='h4'>{t('profile.title')}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {lgUp ?
                            <LgTabs config={config} tab={tab} handleTabChange={handleTabChange} />
                            :
                            <SmTabs config={config} tab={tab} handleTabChange={handleTabChange} />
                        }
                        <Grid item xs={lgUp ? 10 : 12} sx={{ pr: 2 }}>
                            {tab === 0 && renderEditProfile()}
                            {tab === 1 && renderAccount()}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
            <ConfirmationModal
                type='remove'
                title={t('profile.modals.terminate.title')}
                description={t('profile.modals.terminate.description')}
                confirmationText={t('profile.modals.terminate.buttons.confirmation')}
                confirm={deleteUser}
                open={deleteUserModal}
                onClose={() => setDeleteUserModal(false)}
            />
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Profile;